<template>
    <modal-controller-base
        content-class="light-light-background"
        footer
        :no-footer-padding="orderView.__name === 'OrderDelivery'"
    >
        <template #header>
            <custom-header>
                <template #start>
                    <ion-button
                        @click="goBack()"
                        class="purple-light-color"
                        v-if="orderView.__name !== 'OrderSuccess'"
                    >
                        <ion-icon :icon="chevronBack" />
                        {{ $t('button.back') }}
                    </ion-button>
                </template>

                <template v-if="orderView.__name === 'ShoppingCart'">
                    {{ $t('titleRoute.cart') }}
                </template>

                <template v-else>
                    {{ $t('titleRoute.orders') }}
                </template>

                <template #end>
                    <ion-button
                        v-if="orderView.__name !== 'ShoppingCart'"
                        class="purple-light-color"
                        @click="modalController.dismiss()"
                    >
                        {{ $t('button.cancel') }}
                    </ion-button>

                    <ion-button
                        v-else-if="orderView.__name !== 'OrderSuccess'"
                        class="purple-light-color"
                        @click="refreshShoppingCart"
                    >
                        <ion-icon :icon="refreshOutline"></ion-icon>
                    </ion-button>
                </template>
            </custom-header>
        </template>

        <KeepAlive>
            <Component :cartData="cartData" :is="orderView" @next="goNextThrottle" @back="goBack" />
        </KeepAlive>

        <template #footer>
            <ion-toolbar
                v-show="notCart && orderView !== OrderSuccess"
                class="grey4-grey4-background purple-purple-color no-padding-toolbar"
                :class="{'no-padding': orderView.__name === 'OrderDelivery'}"
                style="--border-width: 0;"
            >
                <ion-row
                    v-if="cartData && cartData.supplier.preOrderHint"
                    class="light-light-color p-0 max-width"
                    style="background: var(--ion-color-danger);"
                >
                    <ion-col class="px-3">
                        <div class="fw-b">
                            {{ cartData.supplier.preOrderHint }}
                        </div>
                    </ion-col>
                </ion-row>

                <ion-row v-if="cartData" class="grey4-grey4-background py-2 fs-smaller grey1-grey1-color">
                    <ion-col size="auto" class="d-flex flex-d-c ion-justify-content-end">
                        <ion-row>
                            <ion-col>
                                {{
                                    $t('messages.cart.amountItems', { amount: cartData.supplier?.totalCartItems ?? 0 })
                                }}
                            </ion-col>
                        </ion-row>

                        <ion-row>
                            <ion-col class="py-0 center-y">
                                {{ $t('messages.cart.amount', { amount: cartData.supplier?.totalCartPosition ?? 0 }) }}
                            </ion-col>
                        </ion-row>
                    </ion-col>

                    <ion-col>
                        <div
                            class="ion-justify-content-end d-flex ion-align-items-end flex-d-c"
                            style="gap: 0.5rem; padding-inline: 5px;"
                        >
                            <div
                                v-if="cartData.supplier.showCartTotal && summarySum > 0"
                                class="fw-b purple-purple-color fs-larger ion-text-right"
                            >
                                {{ convertNumberWithComma(summarySum.toFixed(3)) }} {{ CURRENCY_EUR }}
                                {{ $t('object.total') }}
                            </div>

                            <button-base
                                :disabled="(orderView.__name === 'OrderPayment' && !userStore.permission[PERMISSION_KEY.ORDER_CREATE]) || persistentStore.noConnection"
                                class="max-height m-0 wrap-button"
                                style="min-width: 120px;"
                                @click="goNextThrottle"
                            >
                                <template v-if="orderView.__name === 'OrderDelivery'">
                                    {{ $t('button.forward') }}
                                </template>

                                <template v-else>
                                    {{ $t('button.finishOrder') }}
                                </template>
                            </button-base>
                        </div>
                    </ion-col>
                </ion-row>
            </ion-toolbar>
        </template>
    </modal-controller-base>
</template>

<script setup lang="ts">
import { computed, reactive, ref, shallowRef } from "vue";
import { IonButton, IonCol, IonIcon, IonRow, IonToolbar, loadingController, modalController } from "@ionic/vue";
import { route } from "@/router";
import { t } from "@/plugins/vue-i18n";
import { useThrottleFn } from "@vueuse/core";

import ModalControllerBase from "@/components/modal/modalControllerModals/ModalControllerBase.vue";
import ButtonBase from "@/components/button/ButtonBase.vue";
import CustomHeader from "@/components/basePageElements/CustomHeader.vue";
import OrderDelivery from "@/views/user/order/OrderDelivery.vue";
import OrderPayment from "@/views/user/order/OrderPayment.vue";
import ShoppingCart from "@/views/user/ShoppingCart.vue";
import OrderSuccess from "@/views/user/order/OrderSuccess.vue";

import { useListStore } from "@/stores/lists";
import { useUserStore } from "@/stores/user"
import { useSupplierStore } from "@/stores/suppliers";

import { useLoader } from "@/composables/useLoader";

import { popAlertConfirm, popAlertOk } from "@/functions/alert";
import { missingLocation, missingTimingDelivery, missingTimingPickup } from "@/functions/checks";

import { PERMISSION_KEY } from "@/types/user";
import { CartData, Order } from "@/types/supplier";
import { DELIVERY_TYPE } from "@/types/enum/order";
import { GetListItemsParams } from "@/types/list";
import { INDUSTRY_TYPE } from "@/types/enum/supplierEnum";

import { chevronBack, refreshOutline } from "ionicons/icons";
import convertFormat, { diffDates } from "@/functions/luxon";
import { DateTime } from "luxon";
import { DATE_FORMAT } from "@/types/enum/luxonEnum";
import { usePersistentStore } from "@/stores/persistent";
import { convertNumberWithComma } from "@/functions/converter";
import { CURRENCY_EUR } from "@/assets/variables";

const listStore = useListStore()
const userStore = useUserStore()
const supplierStore = useSupplierStore()
const persistentStore = usePersistentStore()
const { showLoading, hideLoading } = useLoader()

const cartData = reactive<CartData>({
    supplier: {
        id: '',
        name: '',
        email: '',
        customerNo: '',
        industry: INDUSTRY_TYPE.PRIVATE,
        settings: {},
        hasSpecialOffers: false,
        totalCartPosition: 0,
        totalCartItems: 0,
        noListCartPosition: 0,
        noListCartItems: 0,
        showCartTotal: false
    },
    lists: [],
    cartItems: [],
    summary: []
})
const orderView = shallowRef(ShoppingCart)
const ordering = ref<boolean>(false)

const notCart = computed<boolean>(() => {
    return orderView.value.__name === 'OrderDelivery'
})
const locationId = computed<string>(() => String(route.value.params.locationId ?? ''))
const listId = computed<string>(() => String(route.value.params.listId ?? ''))

const summarySum = computed(() => cartData.summary.reduce((accumulator, currentValue) => {
    return accumulator + ((currentValue.quantity ?? 0) * (currentValue.item.price ?? 0))
}, 0))

const refreshShoppingCart = async () => {
    await showLoading()
    await Promise.all([
        listStore.getShoppingCart(locationId.value),
        userStore.getMe()
    ])
    await hideLoading()
}

const timeIssue = async (kind: DELIVERY_TYPE = DELIVERY_TYPE.DELIVERY): Promise<void> => {
    await popAlertOk({
        header: t('alert.discardFormTitle'),
        message: (kind === DELIVERY_TYPE.DELIVERY) ? t('label.timeIssueDelivery') : t('label.timeIssuePickup'),
    })
}

const locationIssue = async (): Promise<void> => {
    await popAlertOk({
        header: t('alert.discardFormTitle'),
        message: t('messages.order.locationIssue')
    })
}

const finishOrder = async () => {
    ordering.value = true
    showLoading(t('messages.ordering'))

    const orderItems = []
    orderItems.push(...cartData.cartItems)
    cartData.lists.forEach((list) => {
        orderItems.push(...list.cartItems)
    })
    listStore.order.supplierId = cartData.supplier.id
    listStore.order.items = orderItems

    const response = await listStore.createOrder(locationId.value)

    if (!response) {
        ordering.value = false
        orderView.value = ShoppingCart
        hideLoading()
        return
    }

    orderView.value = OrderSuccess

    try {
        await supplierStore.changeSupplierSettings(
            route.value.params.locationId as string,
            cartData.supplier.id,
            cartData.supplier.settings
        )
    } catch (e) {
        console.error("ERROR SAVING SUPPLIER SETTINGS", e)
    }

    await Promise.all([
        listStore.resetOrder(locationId.value),
        listStore.getAllOrders(locationId.value)
    ])

    if (listId.value) await listStore.getListItems(new GetListItemsParams({
        locationId: locationId.value,
        listId: listId.value
    }))

    ordering.value = false

    hideLoading()
}

const hasRequiredData = (cart: CartData): boolean => {
    const supplierId = cart.supplier?.id;
    const hasPickupLocations = (cart.supplier?.pickupLocations?.length ?? 0) > 0
    const hasDeliveryDays = !!supplierStore.deliveryDays[supplierId];
    const hasPickupDays = !!supplierStore.pickupDays[supplierId];

    return hasPickupLocations ? hasDeliveryDays && hasPickupDays : hasDeliveryDays;
    // return !!supplierStore.deliveryDays[cart.supplier?.id];
}

const goNext = async (item: CartData): Promise<void> => {
    if (item) Object.assign(cartData, item)

    const { showLoading, hideLoading } = useLoader()

    switch (orderView.value.__name) {
        case 'ShoppingCart':
            if (!hasRequiredData(item)) {
                await showLoading(t('label.orderDataLoading'), 5000)
                await supplierStore.getSupplierDeliveryDays(locationId.value, item.supplier.id)
                if (item.supplier.pickupLocations?.length) await supplierStore.getSupplierPickupDays(locationId.value, item.supplier.id)
            }
            orderView.value = OrderDelivery
            listStore.order = new Order()
            if (await loadingController.getTop()) await hideLoading()
            break;
        case 'OrderDelivery':
            orderView.value = OrderPayment
            break;
        case 'OrderPayment':
            if (ordering.value) return
            if (missingLocation()) return await locationIssue()
            if (listStore.order.selectedDeliveryType === DELIVERY_TYPE.DELIVERY && missingTimingDelivery(cartData)) return await timeIssue(DELIVERY_TYPE.DELIVERY)
            if (listStore.order.selectedDeliveryType === DELIVERY_TYPE.PICKUP && missingTimingPickup(cartData)) return await timeIssue(DELIVERY_TYPE.PICKUP)

            if (listStore.order.deliveryDate && (diffDates(DateTime.fromISO(listStore.order.deliveryDate), DateTime.now(), ["days"]).days ?? 0) > 7) {
                popAlertConfirm({
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    message: t('messages.longOrder', { date: convertFormat(listStore.order.deliveryDate, 'iso', DATE_FORMAT.DE) }),
                    okText: t('button.finishOrder'),
                    cancelText: t('button.cancel'),
                    handler: {
                        ok: finishOrder
                    }
                })
                return
            }

            await finishOrder()

            break;
        case 'OrderSuccess':
            modalController.dismiss();
            break;
        default:
            break;
    }
}

const goNextThrottle = useThrottleFn(goNext, 200)

const goBack = (): void => {
    switch (orderView.value.__name) {
        case 'ShoppingCart':
            modalController.dismiss()
            break;
        case 'OrderDelivery':
            orderView.value = ShoppingCart
            break;
        case 'OrderPayment':
            orderView.value = OrderDelivery
            break;
        default:
            break;
    }
}
</script>

<style scoped>
.no-padding {
    padding-top: 0 !important;
    padding-left: var(--ion-safe-area-left) !important;
    padding-right: var(--ion-safe-area-right) !important;

    --padding-start: 0;
    --padding-top: 0;
    --padding-end: 0;
}
</style>
